import React, { useState, useEffect, useCallback, useRef } from 'react';

interface MenuPosition {
  x: number;
  y: number;
}

interface ToggleContextMenuOptions {
  offsetX?: number;
  offsetY?: number;
}

export const useContextMenu = () => {
  const [contextMenuOpen, setContextMenuOpen] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleContextMenu = useCallback(
    (id: string, e: React.MouseEvent, options: ToggleContextMenuOptions = {}) => {
      e.stopPropagation();
      const { offsetX = -60, offsetY = -15 } = options;

      if (contextMenuOpen === id) {
        setContextMenuOpen(null);
        setMenuPosition(null);
      } else {
        setContextMenuOpen(id);
        setMenuPosition({ x: e.clientX + offsetX, y: e.clientY + offsetY });
      }
    },
    [contextMenuOpen],
  );

  const closeContextMenu = useCallback(() => {
    setContextMenuOpen(null);
    setMenuPosition(null);
  }, []);

  // Handle click outside to close menu
  useEffect(() => {
    if (!contextMenuOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeContextMenu();
      }
    };
    document.addEventListener('click', handleClickOutside, { capture: true });
    return () => {
      document.removeEventListener('click', handleClickOutside, {
        capture: true,
      });
    };
  }, [contextMenuOpen, closeContextMenu]);

  // higher order function to handle menu actions in any component
  const handleMenuAction = useCallback(
    (action: () => void) => (e: React.MouseEvent) => {
      e.stopPropagation();
      action();
      closeContextMenu();
    },
    [closeContextMenu],
  );

  return {
    contextMenuOpen,
    toggleContextMenu,
    closeContextMenu,
    menuPosition,
    menuRef,
    handleMenuAction,
  };
};

